import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { IMAGE_MEASUREMENT_MODEL_KINDS, type EntryConfigUnion } from "@/types/entryConfig";
import { ArchiveRestore, Edit } from "lucide-react";
import React from "react";
import { getTraitIcon, getTraitColor, getTraitTypeLabel, sortTraitsByTypeAndName } from "@/lib/traitIcons";
import { cn } from "@/lib/utils";

interface TraitListProps {
  traits: EntryConfigUnion[];
  onEditTrait?: (trait: EntryConfigUnion) => void;
  onRestoreTrait?: (trait: EntryConfigUnion) => void;
  canEdit?: boolean;
  /** "archived" renders greyed rows with a Restore action instead of Edit. */
  variant?: "active" | "archived";
}

const TraitList: React.FC<TraitListProps> = ({
  traits,
  onEditTrait,
  onRestoreTrait,
  canEdit = false,
  variant = "active",
}) => {
  const isArchivedVariant = variant === "archived";
  // Show the actions column when the user has permission and the relevant
  // callback exists: Restore for archived rows, Edit for active rows.
  const showEditAction = canEdit && (isArchivedVariant ? !!onRestoreTrait : !!onEditTrait);
  // Helper function to render options based on entry config type
  const renderTraitOptions = (trait: EntryConfigUnion) => {
    switch (trait.type) {
      case "number":
        return (
          <>
            {trait.numberConfig?.min !== undefined &&
              trait.numberConfig?.max !== undefined &&
              `Range: ${trait.numberConfig.min} - ${trait.numberConfig.max}`}
            {trait.numberConfig?.wholeNumbersOnly && (
              <Badge
                variant="outline"
                className="ml-2 text-primary border-primary">
                Whole numbers only
              </Badge>
            )}
          </>
        );
      case "category":
        return (
          <div className="flex flex-wrap gap-1">
            {trait.categoryConfig?.choices?.map((choice, idx) => (
              <Badge
                key={idx}
                variant="secondary"
                className="mr-1 text-primary bg-primary/10">
                {choice}
              </Badge>
            ))}
          </div>
        );
      case "date":
        return (
          <>
            {trait.dateConfig?.min && `Min: ${trait.dateConfig.min}`}
            {trait.dateConfig?.max && `Max: ${trait.dateConfig.max}`}
          </>
        );
      case "photo": {
        const captureReqs = trait.photoConfig?.imageCaptureRequirements;
        // Photo bounds are individually optional, so surface whichever are set (an unset side shows "any")
        // rather than hiding the constraint entirely when only one bound is present.
        const minDist = captureReqs?.minDistanceMeters;
        const maxDist = captureReqs?.maxDistanceMeters;
        const minPitch = captureReqs?.minPitchAngleDegrees;
        const maxPitch = captureReqs?.maxPitchAngleDegrees;
        const hasDistance = minDist !== undefined || maxDist !== undefined;
        const hasPitch = minPitch !== undefined || maxPitch !== undefined;

        return (
          <div className="flex flex-col gap-1">
            {hasDistance && (
              <div>{`Distance: ${minDist !== undefined ? `${Math.round(minDist * 100)}cm` : "any"} - ${maxDist !== undefined ? `${Math.round(maxDist * 100)}cm` : "any"}`}</div>
            )}
            {hasPitch && (
              <div>{`Pitch: ${minPitch !== undefined ? `${minPitch}°` : "any"} - ${maxPitch !== undefined ? `${maxPitch}°` : "any"}`}</div>
            )}
          </div>
        );
      }
      case "imageMeasurement": {
        const modelOpts = trait.imageMeasurementConfig?.imageMeasurementModelOptions;
        const captureReqs = trait.imageMeasurementConfig?.imageCaptureRequirements;
        const defaultOpts = trait.imageMeasurementConfig?.objectDetectionDefaultOptions;
        const grading = trait.imageMeasurementConfig?.grading;

        return (
          <div className="flex flex-col gap-1">
            {modelOpts?.kind === IMAGE_MEASUREMENT_MODEL_KINDS.OPEN_SET_OBJECT_DETECTOR && modelOpts.prompts?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {modelOpts.prompts.map((name, idx) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="mr-1 text-primary bg-primary/10">
                    {name}
                  </Badge>
                ))}
              </div>
            )}
            {modelOpts?.kind === IMAGE_MEASUREMENT_MODEL_KINDS.BISECTED_LETTUCE && (
              <div className="flex flex-wrap gap-1">
                <Badge
                  variant="secondary"
                  className="mr-1 text-primary bg-primary/10">
                  Bisected Lettuce
                </Badge>
                {modelOpts.confidenceThreshold !== undefined && (
                  <Badge
                    variant="outline"
                    className="text-muted-foreground">
                    {`Confidence: ${Math.round(modelOpts.confidenceThreshold * 100)}%`}
                  </Badge>
                )}
              </div>
            )}
            {grading && grading.buckets.length > 0 && (
              <div>
                <Badge variant="outline" className="border-green-600 bg-green-50 text-green-700">
                  {`${grading.buckets.length} size grades (${grading.dimension})`}
                </Badge>
              </div>
            )}
            {defaultOpts?.minObjectLengthMeters !== undefined &&
              defaultOpts?.maxObjectLengthMeters !== undefined && (
                <div>{`Length: ${defaultOpts.minObjectLengthMeters} - ${defaultOpts.maxObjectLengthMeters}m`}</div>
              )}
            {captureReqs?.minDistanceMeters !== undefined &&
              captureReqs?.maxDistanceMeters !== undefined && (
                <div>{`Distance: ${Math.round(captureReqs.minDistanceMeters * 100)} - ${Math.round(captureReqs.maxDistanceMeters * 100)}cm`}</div>
              )}
            {captureReqs?.minPitchAngleDegrees !== undefined &&
              captureReqs?.maxPitchAngleDegrees !== undefined && (
                <div>{`Pitch: ${captureReqs.minPitchAngleDegrees}° - ${captureReqs.maxPitchAngleDegrees}°`}</div>
              )}
          </div>
        );
      }
      case "standCountMeasurement":
        return (
          <>
            {trait.standCountMeasurementConfig?.imgMeasureConfig?.imageMeasurementModelOptions?.kind === "none" && (
              <Badge variant="outline" className="mr-2 text-amber-600 border-amber-600">Manual annotation</Badge>
            )}
            {trait.standCountMeasurementConfig?.boxLengthMeters !== undefined &&
              `Box Length: ${trait.standCountMeasurementConfig.boxLengthMeters}m`}
            {trait.standCountMeasurementConfig?.boxWidthMeters !== undefined &&
              ` Width: ${trait.standCountMeasurementConfig.boxWidthMeters}m`}
          </>
        );
      case "leafAreaIndex": {
        const config = trait.leafAreaIndexConfig;
        const hasFixedDimensions =
          config?.boxLengthMeters !== undefined && config.boxWidthMeters !== undefined;
        return (
          <div>
            {config?.defaultSensitivity !== undefined && <div>{`Sensitivity: ${config.defaultSensitivity}`}</div>}
            {config?.useFixedBoxSize ? (
              <div>
                Fixed box
                {hasFixedDimensions && `: ${config.boxLengthMeters}m × ${config.boxWidthMeters}m`}
              </div>
            ) : (
              <div>On-screen box</div>
            )}
          </div>
        );
      }
      case "ruler":
        return (
          <>
            {trait.rulerConfig?.minLengthMeters !== undefined &&
              trait.rulerConfig?.maxLengthMeters !== undefined &&
              `Range: ${trait.rulerConfig.minLengthMeters} - ${trait.rulerConfig.maxLengthMeters}m`}
          </>
        );
      case "timer":
        return (
          <>
            {trait.timerConfig?.startStopOnBarcodeScan && (
              <Badge
                variant="outline"
                className="text-primary border-primary">
                Barcode start/stop
              </Badge>
            )}
          </>
        );
      case "rowPlantedStandCount": {
        const config = trait.rowPlantedStandCountConfig;
        const modelOpts = config?.modelOptions;
        const isOpenSet = modelOpts?.kind === "openSet";
        const detectionPrompt = isOpenSet ? modelOpts.detectionPrompt : undefined;

        return (
          <div className="flex flex-col gap-1 text-sm">
            {/* Model info */}
            <div className="flex flex-wrap gap-1 items-center">
              <Badge variant="secondary" className="text-primary bg-primary/10">
                {isOpenSet ? "Open Set" : "Generic Crop"}
              </Badge>
              {detectionPrompt && (
                <Badge variant="outline" className="text-muted-foreground">
                  {detectionPrompt}
                </Badge>
              )}
            </div>
            {/* Walk settings */}
            <div className="text-muted-foreground">
              {config?.expectedPlantLineCount !== undefined && (
                <span>Lines: {config.expectedPlantLineCount}</span>
              )}
              {config?.walkDistanceMetersTarget !== undefined && (
                <span className="ml-2">Walk: {config.walkDistanceMetersTarget}m</span>
              )}
              {config?.isWalkDistanceRequired && (
                <Badge variant="outline" className="ml-2 text-xs">Required</Badge>
              )}
            </div>
            {/* Advanced settings summary */}
            {(config?.minPlantSpacingMeters !== undefined ||
              config?.minDetectionDistanceMeters !== undefined ||
              config?.maxDetectionDistanceMeters !== undefined) && (
              <div className="text-muted-foreground text-xs">
                {config?.minPlantSpacingMeters !== undefined && (
                  <span>Spacing: {Math.round(config.minPlantSpacingMeters * 100)}cm</span>
                )}
                {config?.minDetectionDistanceMeters !== undefined &&
                  config?.maxDetectionDistanceMeters !== undefined && (
                  <span className="ml-2">
                    Distance: {Math.round(config.minDetectionDistanceMeters * 100)}-{Math.round(config.maxDetectionDistanceMeters * 100)}cm
                  </span>
                )}
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="w-full overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Options</TableHead>
            {showEditAction && <TableHead>Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {traits.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={showEditAction ? 5 : 4}
                className="h-24 text-center">
                No traits found. Add your first trait!
              </TableCell>
            </TableRow>
          ) : (
            sortTraitsByTypeAndName(traits).map((trait) => {
              const Icon = getTraitIcon(trait.type);
              const traitColor = getTraitColor(trait.type);
              return (
                <TableRow
                  key={trait.id}
                  className={cn(isArchivedVariant && "opacity-60 text-muted-foreground")}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Icon className={cn("h-4 w-4", traitColor)} aria-hidden="true" />
                      <span>{trait.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-primary flex items-center gap-1.5 w-fit">
                      <Icon className={cn("h-3.5 w-3.5", traitColor)} aria-hidden="true" />
                      <span>{getTraitTypeLabel(trait.type)}</span>
                    </Badge>
                  </TableCell>
                  <TableCell>{trait.description}</TableCell>
                  <TableCell>{renderTraitOptions(trait)}</TableCell>
                  {showEditAction && (
                    <TableCell>
                      {isArchivedVariant ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onRestoreTrait?.(trait)}
                          className="h-8 gap-1.5">
                          <ArchiveRestore className="h-4 w-4" />
                          Restore
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditTrait?.(trait)}
                          className="h-8 w-8 p-0">
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default TraitList;
